import React from 'react';
import { Button, DialogActions, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import type { ButtonProps, DialogActionsProps } from '@fluentui/react-components';

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
    flexDialogActions: {
        flexDirection: 'row',
        justifySelf: 'flex-end',
    },
    // Primary button (Spiris theme)
    primaryButton: {
        padding: '10px 20px',
        borderTopStyle: 'none',
        borderRightStyle: 'none',
        borderBottomStyle: 'none',
        borderLeftStyle: 'none',
        textAlign: 'center',
        textDecorationLine: 'none',
        display: 'inline-block',
        fontSize: tokens.fontSizeBase400,
        cursor: 'pointer',
        transitionProperty: 'background-color',
        transitionDuration: '0.4s',
        borderRadius: tokens.borderRadiusXLarge,
        backgroundColor: tokens.colorBrandBackground,
        color: tokens.colorNeutralBackground1,
        '&:hover': {
            backgroundColor: tokens.colorBrandBackgroundHover,
            color: tokens.colorNeutralBackground1,
        },
        '&:active': {
            backgroundColor: tokens.colorBrandBackgroundPressed,
            color: tokens.colorNeutralBackground1,
        },
        '&:focus': {
            boxShadow: `0 0 0 2px ${tokens.colorNeutralForegroundOnBrand} inset`,
            outline: 'none',
        },
    },
    // Secondary button (Spiris theme)
    secondaryButton: {
        padding: '10px 20px',
        textAlign: 'center',
        textDecorationLine: 'none',
        display: 'inline-block',
        fontSize: tokens.fontSizeBase400,
        cursor: 'pointer',
        transitionProperty: 'border-color, background-color',
        transitionDuration: '0.4s',
        borderRadius: tokens.borderRadiusXLarge,
        borderTopStyle: 'none',
        borderRightStyle: 'none',
        borderBottomStyle: 'none',
        borderLeftStyle: 'none',
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorBrandForegroundLink,
        '&:hover': {
            backgroundColor: tokens.colorBrandBackground2,
            color: tokens.colorBrandForegroundLinkHover,
        },
        '&:focus': {
            boxShadow: `0 0 0 2px ${tokens.colorBrandForegroundLink} inset`,
            backgroundColor: tokens.colorBrandBackground2,
            outline: 'none',
        },
        '&:focus-visible': {
            boxShadow: `0 0 0 2px ${tokens.colorBrandForegroundLink} inset`,
            backgroundColor: tokens.colorBrandBackground2,
            outline: 'none',
        },
    },
    spinnerContainer: {
        width: '100%',
        height: '10px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        transform: 'scale(0.5)',
    },
});

// =============================================================================
// COMPONENTS
// =============================================================================

export const FlexDialogActions: React.FC<DialogActionsProps> = ({ className, ...props }) => {
    const classes = useStyles();
    return <DialogActions className={mergeClasses(classes.flexDialogActions, className)} {...props} />;
};

export const PrimaryButton: React.FC<ButtonProps> = ({ className, ...props }) => {
    const classes = useStyles();
    return <Button className={mergeClasses(classes.primaryButton, className)} {...props} />;
};

export const SecondaryButton: React.FC<ButtonProps> = ({ className, ...props }) => {
    const classes = useStyles();
    return <Button className={mergeClasses(classes.secondaryButton, className)} {...props} />;
};

interface SpinnerContainerProps {
    children?: React.ReactNode;
    className?: string;
}

export const SpinnerContainer: React.FC<SpinnerContainerProps> = ({ children, className }) => {
    const classes = useStyles();
    return <div className={mergeClasses(classes.spinnerContainer, className)}>{children}</div>;
};
